'use client';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { Box, Theme } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement } from 'react';
import { toEntityInitials } from './toEntityInitials';

/**
 * How an identity stands for the request: `shared` is already shared with this provider.
 * The parent decides the state; the components only draw it.
 */
export type RequestIdentityState = 'verified' | 'submitted' | 'draft' | 'shared';

export type EntityInitialsAvatarProps = {
  name: string;
  state: RequestIdentityState;
};

const BADGE_ICON: Record<RequestIdentityState, typeof CheckCircleIcon> = {
  verified: CheckCircleIcon,
  shared: CheckCircleIcon,
  submitted: HourglassTopIcon,
  draft: NoteAddIcon,
};

// An already-shared identity is a verified one, so it reads as verified on the badge.
const BADGE_PALETTE_KEY = {
  verified: 'verified',
  shared: 'verified',
  submitted: 'inProgress',
  draft: 'draft',
} as const;

/**
 * A round initials avatar with a small status badge at its lower left. Decorative: the card that
 * holds it names the entity and says its status in text, so this is hidden from assistive
 * technology.
 */
function EntityInitialsAvatarComponent({
  name,
  state,
}: Readonly<EntityInitialsAvatarProps>): ReactElement {
  const BadgeIcon = BADGE_ICON[state];
  const paletteKey = BADGE_PALETTE_KEY[state];

  return (
    <Box
      aria-hidden="true"
      sx={(theme: Theme) => ({
        position: 'relative',
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: theme.identityCard.avatarSize,
        height: theme.identityCard.avatarSize,
        borderRadius: theme.semantic.borderRadius.round,
        backgroundColor: theme.palette.surface.brandSubtle,
        color: theme.palette.primary.main,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.semantic.fontSize.lg,
        fontWeight: theme.semantic.fontWeight.semibold,
        lineHeight: theme.semantic.lineHeight.standard,
      })}
    >
      {toEntityInitials(name)}
      <Box
        sx={(theme: Theme) => ({
          position: 'absolute',
          left: `calc(-1 * ${theme.semantic.spacing['3xs']})`,
          bottom: `calc(-1 * ${theme.semantic.spacing['2xs']})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: theme.semantic.size['8xs'],
          height: theme.semantic.size['8xs'],
          borderRadius: theme.semantic.borderRadius.round,
          backgroundColor: theme.palette.entity[paletteKey].fill,
          color: theme.palette.entity[paletteKey].text,
        })}
      >
        <BadgeIcon sx={(theme: Theme) => ({ fontSize: theme.semantic.fontSize.xs })} />
      </Box>
    </Box>
  );
}

export const EntityInitialsAvatar = withClarityTheme(EntityInitialsAvatarComponent);
